/**
 * Raummodell Raum 33 — Vertiefungs-Block „raummodell" der Referenzseiten
 * (snippets/referenz-raummodell.liquid). WebGL-Modell über der SVG-Draufsicht,
 * Bauteilauswahl, Kamera. Geometrie und Maße unverändert aus der geprüften Raumseite
 * des Übergabepakets (Robin, 25.09.2026, Raumskizze vom 17.09.2026).
 * Ohne WebGL bleiben Draufsicht und alle Bauteil-Texte sichtbar.
 */
(() => {
  const root = document.querySelector('[data-ref-modell]');
  if (!root) return;

  const $ = (s) => root.querySelector(s);
  const $$ = (s) => [...root.querySelectorAll(s)];
  const motion = matchMedia('(prefers-reduced-motion: reduce)');

  /*
     Raumorientierung wie der Grundriss vom 17.09.2026:
     x: links (hintere Tafel/Tür) nach rechts (Unterrichtstafel).
     z: oben (Fensterfront) nach unten (langer 30-cm-Wandstreifen).
     y: Höhe. Nicht bemaßte Positionen sind aus Skizze und Fotos angenähert. */
  const room = { length: 8.97, width: 8.05, height: 3.2 };
  const FOKUS_FARBE = [0.031, 0.49, 0.459]; // --teal-700 #087d75

  function raumObjekte() {
    const { length: L, width: B, height: H } = room;
    const objects = [];
    const add = (id, group, x, y, z, dx, dy, dz, color, wall) => {
      objects.push({ id, group, x, y, z, dx, dy, dz, color, wall });
    };
    const plaster = [0.86, 0.84, 0.79];
    const floor = [0.69, 0.61, 0.5];
    const felt = [0.44, 0.46, 0.45];
    const rear = [0.51, 0.5, 0.47];
    const white = [0.98, 0.96, 0.91];
    const metal = [0.61, 0.62, 0.6];
    const glass = [0.67, 0.76, 0.75];
    const board = [0.16, 0.25, 0.2];

    add('boden', 'base', 0, -0.1, 0, L, 0.1, B, floor);

    // Fensterwand mit vier Ausschnitten, nach der Draufsicht angeordnet.
    add('fenster-bruestung', 'base', 0, 0, -0.15, L, 0.55, 0.15, plaster, 'north');
    add('fenster-sturz', 'base', 0, 3.04, -0.15, L, 0.16, 0.15, plaster, 'north');
    const windows = [0.013, 0.243, 0.473, 0.703].map((x) => ({ x: x * L, w: 0.212 * L }));
    let edge = 0;
    windows.forEach((w, i) => {
      if (w.x > edge)
        add(
          `fenster-pfeiler-${i}`,
          'base',
          edge,
          0.55,
          -0.15,
          w.x - edge,
          2.49,
          0.15,
          plaster,
          'north',
        );
      add(`glas-${i}`, 'base', w.x, 0.62, -0.11, w.w, 2.3, 0.025, glass, 'north');
      add(
        `fenster-sprosse-${i}`,
        'base',
        w.x + w.w / 2 - 0.022,
        0.62,
        -0.065,
        0.044,
        2.3,
        0.04,
        white,
        'north',
      );
      add(`fenster-quer-${i}`, 'base', w.x, 1.73, -0.06, w.w, 0.035, 0.04, white, 'north');
      add(`fensterbank-${i}`, 'base', w.x, 0.54, -0.1, w.w, 0.045, 0.27, white, 'north');
      add(`fenster-50-${i}`, 'window', w.x, 2.98, -0.1, w.w, 0.05, 0.34, felt);
      edge = w.x + w.w;
    });
    add('fenster-rest', 'base', edge, 0.55, -0.15, L - edge, 2.49, 0.15, plaster, 'north');
    add('tafelwand', 'base', L, 0, 0, 0.15, H, B, plaster, 'east');
    add('laengswand', 'base', 0, 0, B, L, H, 0.15, plaster, 'south');

    // Tür in der hinteren Wand, im Grundriss links unten.
    const doorA = B * 0.825;
    const doorB = B * 0.965;
    add('rueckwand-a', 'base', -0.15, 0, 0, 0.15, H, doorA, plaster, 'west');
    add('rueckwand-b', 'base', -0.15, 0, doorB, 0.15, H, B - doorB, plaster, 'west');
    add('tuersturz', 'base', -0.15, 2.12, doorA, 0.15, H - 2.12, doorB - doorA, plaster, 'west');
    add('tuerblatt', 'base', -1.05, 0, doorB - 0.035, 1.05, 2.08, 0.04, [0.7, 0.67, 0.61]);

    // Unterrichtstafel rechts, zwei 70 cm hohe Streifen seitlich oberhalb.
    add('tafel-rahmen', 'base', L - 0.1, 0.65, B * 0.38, 0.07, 1.27, B * 0.245, metal);
    add('unterrichtstafel', 'base', L - 0.12, 0.69, B * 0.385, 0.023, 1.19, B * 0.235, board);
    add('tafel-50-fensterseite', 'front', L - 0.05, 2.13, B * 0.08, 0.05, 0.7, B * 0.255, felt);
    add('tafel-50-bis-ecke', 'front', L - 0.05, 2.13, B * 0.67, 0.05, 0.7, B * 0.33, felt);
    add('laengswand-50', 'long', 0, 2.23, B - 0.05, L, 0.3, 0.05, felt);

    // Ausschließlich 25 mm an der hinteren Tafelwand. Ausschnitte für Tafel und Tür.
    const start = B * 0.29;
    const ba = B * 0.41;
    const bb = B * 0.67;
    const y0 = 1.16;
    const by = 1.26;
    const bh = 1.08;
    const top = 3.06;
    add(
      'hintere-tafel-rahmen',
      'base',
      0.03,
      by - 0.04,
      ba - 0.04,
      0.045,
      bh + 0.08,
      bb - ba + 0.08,
      metal,
    );
    add('hintere-tafel', 'base', 0.079, by, ba, 0.018, bh, bb - ba, board);
    add(
      'rueckwand-25-oberrand',
      'rear',
      0,
      by + bh + 0.04,
      start,
      0.025,
      top - (by + bh + 0.04),
      B - start,
      rear,
    );
    add(
      'rueckwand-25-seite-a',
      'rear',
      0,
      y0,
      start,
      0.025,
      by + bh + 0.04 - y0,
      ba - 0.04 - start,
      rear,
    );
    add(
      'rueckwand-25-seite-b',
      'rear',
      0,
      y0,
      bb + 0.04,
      0.025,
      by + bh + 0.04 - y0,
      doorA - (bb + 0.04),
      rear,
    );
    add(
      'rueckwand-25-ueber-tuer',
      'rear',
      0,
      2.14,
      doorA,
      0.025,
      by + bh + 0.04 - 2.14,
      doorB - doorA,
      rear,
    );
    add('rueckwand-25-ecke', 'rear', 0, y0, doorB, 0.025, by + bh + 0.04 - y0, B - doorB, rear);
    add(
      'rueckwand-25-unter-tafel',
      'rear',
      0,
      y0,
      ba - 0.04,
      0.025,
      by - 0.04 - y0,
      bb - ba + 0.08,
      rear,
    );

    // Drei Paare entlang der langen x-Achse, Gesamtbreite der Paarzone ca. 3 m.
    const xs = [0.095, 0.353, 0.606].map((v) => v * L);
    const zs = [B / 2 - 1.5, B / 2 + 0.5];
    xs.forEach((x, i) => {
      zs.forEach((z, j) => {
        add(`segel-${i}-${j}`, 'ceiling', x, H - 0.3, z, 1.5, 0.04, 1, white);
        for (const px of [x + 0.13, x + 1.37]) {
          for (const pz of [z + 0.12, z + 0.88]) {
            add(
              `seil-${i}-${j}-${px}-${pz}`,
              'ceiling',
              px,
              H - 0.26,
              pz,
              0.007,
              0.26,
              0.007,
              metal,
            );
          }
        }
      });
    });

    // U-förmige Leuchtenleiste um sämtliche Segel. Sie bleibt in jedem Fokus stehen.
    const railEnd = L * 0.835;
    const railA = B * 0.25;
    const railB = B * 0.74;
    add('leuchtenleiste-oben', 'fixture', 0, H - 0.11, railA, railEnd, 0.055, 0.055, metal);
    add('leuchtenleiste-unten', 'fixture', 0, H - 0.11, railB, railEnd, 0.055, 0.055, metal);
    add(
      'leuchtenleiste-vorn',
      'fixture',
      railEnd - 0.055,
      H - 0.11,
      railA,
      0.055,
      0.055,
      railB - railA + 0.055,
      metal,
    );
    for (const z of [railA, railB]) {
      for (let i = 0; i < 4; i++)
        add(
          `leuchte-${z}-${i}`,
          'fixture',
          0.25 + i * 1.75,
          H - 0.117,
          z + 0.008,
          1.2,
          0.007,
          0.039,
          white,
        );
    }
    add('beamer', 'fixture', xs[2] + 0.5, H - 0.59, B / 2 - 0.15, 0.4, 0.18, 0.3, [0.19, 0.2, 0.2]);
    add(
      'beamerhalter',
      'fixture',
      xs[2] + 0.67,
      H - 0.41,
      B / 2 - 0.025,
      0.045,
      0.41,
      0.045,
      metal,
    );
    return objects;
  }

  const labels = {
    all: 'Alle Absorber',
    ceiling: 'Deckensegel · 40 mm',
    window: 'Fensterausschnitte · 50 mm',
    front: 'Unterrichtstafel · 50 mm',
    long: 'Langer Wandstreifen · 50 mm',
    rear: 'Hintere Tafel · 25 mm',
  };
  const details = $$('[data-rm-detail]');
  const focusButtons = $$('[data-rm-focus]');
  const state = $('[data-rm-state]');
  let focus = 'all';

  // Ohne JavaScript stehen alle Bauteil-Texte untereinander; mit JavaScript nur der gewählte.
  const zeigeDetail = () => {
    details.forEach((el) => {
      el.hidden = el.dataset.rmDetail !== focus;
    });
    focusButtons.forEach((b) => {
      b.setAttribute('aria-pressed', String(b.dataset.rmFocus === focus));
    });
  };
  const statischeDraufsicht = () => {
    root.classList.remove('is-webgl');
    $$('.ref-modell__ansicht, .ref-modell__kamera, .ref-modell__fokus').forEach((el) => {
      el.hidden = true;
    });
    details.forEach((el) => {
      el.hidden = false;
    });
    if (state) state.textContent = 'Statische Draufsicht';
  };

  const canvas = $('[data-rm-canvas]');
  const stage = $('[data-rm-stage]');
  const gl = canvas?.getContext('webgl', {
    alpha: true,
    antialias: true,
    premultipliedAlpha: false,
  });
  if (!canvas || !stage || !gl) {
    statischeDraufsicht();
    return;
  }

  const roomObjects = raumObjekte();
  // Start in der räumlichen Ansicht (Vorgabe Julian, 2026-09-28); die Draufsicht ist
  // die zweite Wahl und bleibt ohne WebGL als SVG stehen.
  const SPACE_YAW = -0.65;
  const SPACE_PITCH = 0.64;
  let yaw = SPACE_YAW;
  let pitch = SPACE_PITCH;
  let targetYaw = SPACE_YAW;
  let targetPitch = SPACE_PITCH;
  let raf = 0;
  let drag = null;
  let mode = 'space';

  const shader = (type, source) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, source);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  };
  const vertex = `attribute vec3 position;attribute vec3 normal;uniform vec3 origin;uniform vec3 dimensions;uniform vec2 room;uniform float yaw;uniform float pitch;uniform vec2 scale;uniform vec3 tint;uniform float opacity;varying vec4 vColor;
void main(){vec3 p=position*dimensions+origin;vec3 q=p-vec3(room.x*.5,0.,room.y*.5);float a=q.x*cos(yaw)-q.z*sin(yaw);float d=q.x*sin(yaw)+q.z*cos(yaw);float sy=q.y*cos(pitch)-d*sin(pitch);float depth=d*cos(pitch)+q.y*sin(pitch);gl_Position=vec4(a*scale.x,(sy-1.5*cos(pitch))*scale.y,-depth/30.,1.);float light=.76+.24*max(0.,dot(normal,normalize(vec3(-.3,1.,.4))));vColor=vec4(tint*light,opacity);}`;
  const fragment = 'precision mediump float;varying vec4 vColor;void main(){gl_FragColor=vColor;}';

  let u;
  try {
    const program = gl.createProgram();
    gl.attachShader(program, shader(gl.VERTEX_SHADER, vertex));
    gl.attachShader(program, shader(gl.FRAGMENT_SHADER, fragment));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS))
      throw new Error(gl.getProgramInfoLog(program));
    gl.useProgram(program);

    const verts = [];
    const quad = (a, b, c, d, n) => {
      for (const p of [a, b, c, a, c, d]) verts.push(...p, ...n);
    };
    quad([0, 1, 0], [0, 1, 1], [1, 1, 1], [1, 1, 0], [0, 1, 0]);
    quad([0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1], [0, -1, 0]);
    quad([0, 0, 0], [0, 1, 0], [1, 1, 0], [1, 0, 0], [0, 0, -1]);
    quad([1, 0, 1], [1, 1, 1], [0, 1, 1], [0, 0, 1], [0, 0, 1]);
    quad([0, 0, 1], [0, 1, 1], [0, 1, 0], [0, 0, 0], [-1, 0, 0]);
    quad([1, 0, 0], [1, 1, 0], [1, 1, 1], [1, 0, 1], [1, 0, 0]);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verts), gl.STATIC_DRAW);
    for (const [name, offset] of [
      ['position', 0],
      ['normal', 12],
    ]) {
      const loc = gl.getAttribLocation(program, name);
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 3, gl.FLOAT, false, 24, offset);
    }
    u = {};
    for (const n of ['origin', 'dimensions', 'room', 'yaw', 'pitch', 'scale', 'tint', 'opacity'])
      u[n] = gl.getUniformLocation(program, n);
  } catch {
    statischeDraufsicht();
    return;
  }

  gl.enable(gl.DEPTH_TEST);
  gl.depthFunc(gl.LEQUAL);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  const wallNear = (w) => {
    if (pitch > 1.48) return false;
    return (
      (w === 'east' && Math.sin(yaw) > 0.05) ||
      (w === 'west' && Math.sin(yaw) < -0.05) ||
      (w === 'south' && Math.cos(yaw) > 0.05) ||
      (w === 'north' && Math.cos(yaw) < -0.05)
    );
  };

  function draw() {
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.uniform2f(u.room, room.length, room.width);
    gl.uniform1f(u.yaw, yaw);
    gl.uniform1f(u.pitch, pitch);
    const units =
      mode === 'plan'
        ? Math.min(canvas.width / 11.5, canvas.height / 10)
        : Math.min(canvas.width / 13.3, canvas.height / 10.4);
    gl.uniform2f(u.scale, (units * 2) / canvas.width, (units * 2) / canvas.height);
    const render = (o, alpha, color) => {
      gl.uniform3f(u.origin, o.x, o.y, o.z);
      gl.uniform3f(u.dimensions, o.dx, o.dy, o.dz);
      gl.uniform3fv(u.tint, color || o.color);
      gl.uniform1f(u.opacity, alpha);
      gl.drawArrays(gl.TRIANGLES, 0, 36);
    };
    const transparent = [];
    for (const o of roomObjects) {
      const absorber = !['base', 'fixture'].includes(o.group);
      let alpha = 1;
      if (o.wall && wallNear(o.wall)) alpha = 0.085;
      if (absorber && focus !== 'all' && o.group !== focus) alpha = 0.22;
      const color = absorber && focus === o.group ? FOKUS_FARBE : o.color;
      if (alpha < 1) transparent.push({ o, alpha, color });
      else render(o, alpha, color);
    }
    // Rückseitige Flächen werden durchsichtig, damit ihre tatsächliche Lage erhalten bleibt.
    const tiefe = (o) =>
      (o.x - room.length / 2) * Math.sin(yaw) + (o.z - room.width / 2) * Math.cos(yaw);
    gl.depthMask(false);
    transparent.sort((a, b) => tiefe(a.o) - tiefe(b.o));
    for (const t of transparent) render(t.o, t.alpha, t.color);
    gl.depthMask(true);
  }

  const orbitButtons = $$('[data-rm-orbit]');
  function updateState() {
    stage.classList.toggle('is-space', mode === 'space');
    $$('[data-rm-view]').forEach((b) => {
      b.setAttribute('aria-pressed', String(b.dataset.rmView === mode));
    });
    orbitButtons.forEach((b) => {
      b.disabled = mode === 'plan';
    });
    if (state)
      state.textContent = `${mode === 'plan' ? 'Draufsicht' : 'Räumliche Ansicht'} · ${labels[focus]}`;
  }
  function animate() {
    cancelAnimationFrame(raf);
    if (motion.matches) {
      yaw = targetYaw;
      pitch = targetPitch;
      draw();
      return;
    }
    const tick = () => {
      yaw += (targetYaw - yaw) * 0.13;
      pitch += (targetPitch - pitch) * 0.13;
      draw();
      if (Math.abs(targetYaw - yaw) + Math.abs(targetPitch - pitch) > 0.002)
        raf = requestAnimationFrame(tick);
      else {
        yaw = targetYaw;
        pitch = targetPitch;
        draw();
      }
    };
    tick();
  }
  function view(which) {
    mode = which;
    targetYaw = which === 'plan' ? 0 : SPACE_YAW;
    targetPitch = which === 'plan' ? Math.PI / 2 : SPACE_PITCH;
    updateState();
    animate();
  }
  function resize() {
    const r = stage.getBoundingClientRect();
    const ratio = Math.min(devicePixelRatio, 2);
    canvas.width = Math.round(r.width * ratio);
    canvas.height = Math.round(r.height * ratio);
    draw();
  }

  root.classList.add('is-webgl');
  new ResizeObserver(resize).observe(stage);
  resize();
  zeigeDetail();
  updateState();

  $$('[data-rm-view]').forEach((b) => {
    b.addEventListener('click', () => view(b.dataset.rmView));
  });
  $('[data-rm-reset]')?.addEventListener('click', () => view('space'));
  orbitButtons.forEach((b) => {
    b.addEventListener('click', () => {
      targetYaw = yaw + Number(b.dataset.rmOrbit) * 0.26;
      animate();
    });
  });
  canvas.addEventListener('pointerdown', (e) => {
    if (mode === 'plan') return;
    cancelAnimationFrame(raf);
    drag = { x: e.clientX, y: e.clientY, yaw, pitch };
    canvas.setPointerCapture(e.pointerId);
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!drag) return;
    yaw = targetYaw = drag.yaw + (e.clientX - drag.x) * 0.006;
    pitch = targetPitch = Math.max(0.3, Math.min(1.35, drag.pitch + (e.clientY - drag.y) * 0.003));
    draw();
  });
  canvas.addEventListener('pointerup', () => {
    drag = null;
  });
  canvas.addEventListener('pointercancel', () => {
    drag = null;
  });
  focusButtons.forEach((b) => {
    b.addEventListener('click', () => {
      focus = b.dataset.rmFocus;
      zeigeDetail();
      updateState();
      draw();
    });
  });
  motion.addEventListener('change', () => {
    if (motion.matches) {
      cancelAnimationFrame(raf);
      yaw = targetYaw;
      pitch = targetPitch;
      draw();
    }
  });
  canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    cancelAnimationFrame(raf);
    statischeDraufsicht();
  });
})();
